import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { AppShell } from "@/components/AppShell";
import { meQuery, requireAuth } from "@/lib/auth-guard";
import type { UserResponse } from "@/api/auth";
import {
  revokeFamilyLink,
  type FamilyLinkResponse,
} from "@/api/familyLinks";
import type { OwnerDisposition } from "@/api/families";
import { extractApiError } from "@/api/client";
import { familiesQuery, familyLinksQuery, invitesQuery } from "@/lib/queries";
import { countLabel } from "@/lib/format";
import {
  Avatar,
  Card,
  DangerButton,
  EmptyCard,
  ErrorText,
  Hairline,
  InkButton,
  LoadingBlock,
  QuietButton,
  RadioRow,
  SectionLabel,
} from "@/components/ui";
import { Modal } from "@/components/Modal";
import { InviteDialog, PendingInvitesSection } from "@/components/invites";

/**
 * Extended Family Circle — mirrors iOS LinksScreen: active links with
 * owner-only Unlink (disposition picker), Connect to Extended Family
 * (link_efc share-link invite), pending EFC invites. Sharing policy
 * per link lives on /sharing, not here (single concern, single
 * location — same rule iOS follows).
 */

const LinksPage = () => {
  const { data: user } = useSuspenseQuery<UserResponse>(meQuery);
  return (
    <AppShell user={user}>
      <LinksView user={user} />
    </AppShell>
  );
};

const LinksView = ({ user }: { user: UserResponse }) => {
  const queryClient = useQueryClient();
  const families = useQuery(familiesQuery);
  const links = useQuery(familyLinksQuery);
  const invites = useQuery(invitesQuery(user.home_family_id));
  const [connecting, setConnecting] = useState(false);
  const [revoking, setRevoking] = useState<FamilyLinkResponse | null>(null);

  const home = families.data?.find(
    (f) => f.family_id === user.home_family_id,
  );
  const isOwner =
    home?.members.find((m) => m.user_id === user.user_id)?.role === "owner";

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["family-links"] });
    void queryClient.invalidateQueries({
      queryKey: ["invites", user.home_family_id],
    });
    void queryClient.invalidateQueries({ queryKey: ["sharing-policies"] });
  };

  if (links.isPending || families.isPending) {
    return <LoadingBlock label="Loading your Extended Family…" />;
  }

  return (
    <div className="mx-auto max-w-2xl">
      <header>
        <h1
          className="font-serif text-[28px] leading-[34px] text-ink"
          style={{ fontWeight: 600 }}
        >
          Your Extended Family
        </h1>
        <p className="mt-1 font-serif text-[14px] italic text-ink-soft">
          Share your memories with your extended family.
        </p>
      </header>

      <section className="mt-8">
        {links.isError ? (
          <EmptyCard
            eyebrow="Couldn't load"
            blurb="We couldn't load your Extended Family circles. Please try again. If this keeps happening, please screenshot this and share with support."
          />
        ) : links.data.length === 0 ? (
          <EmptyCard
            eyebrow="No links yet"
            title="You don't have any Extended Family circles yet."
            blurb="Each person chooses what to share. Your circles stay separate; memories flow when you choose."
          />
        ) : (
          <Card>
            {links.data.map((link, i) => (
              <div key={link.link_id}>
                {i > 0 && <Hairline />}
                <div className="flex flex-wrap items-center gap-4 px-5 py-4">
                  <Avatar
                    name={link.other_family_name}
                    url={link.other_family_avatar_url}
                    size={46}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-sans text-[15px] font-medium text-ink">
                      {link.other_family_name ?? "Another circle"}
                    </p>
                    <p className="font-sans text-[13px] text-ink-soft">
                      {link.other_family_owner_name
                        ? `Owned by ${link.other_family_owner_name} · `
                        : ""}
                      {countLabel(link.other_family_member_count, "member")}
                    </p>
                  </div>
                  {isOwner && (
                    <DangerButton onClick={() => setRevoking(link)}>
                      Unlink
                    </DangerButton>
                  )}
                </div>
              </div>
            ))}
          </Card>
        )}
      </section>

      {home?.my_permissions.invite_efc && (
        <div className="mt-6">
          <InkButton onClick={() => setConnecting(true)}>
            Connect to Extended Family
          </InkButton>
        </div>
      )}

      <PendingInvitesSection
        familyId={user.home_family_id}
        invites={invites.data ?? []}
        intent="link_efc"
        canManage={home?.my_permissions.invite_efc ?? false}
        canResend
        currentUserId={user.user_id}
        isOwner={isOwner ?? false}
        onChanged={refresh}
      />

      {connecting && (
        <InviteDialog
          familyId={user.home_family_id}
          intent="link_efc"
          title="Connect to Extended Family"
          onClose={() => setConnecting(false)}
          onCompleted={refresh}
        />
      )}

      {revoking && (
        <UnlinkDialog
          link={revoking}
          onClose={() => setRevoking(null)}
          onDone={() => {
            setRevoking(null);
            refresh();
          }}
        />
      )}
    </div>
  );
};

const UnlinkDialog = ({
  link,
  onClose,
  onDone,
}: {
  link: FamilyLinkResponse;
  onClose: () => void;
  onDone: () => void;
}) => {
  const [disposition, setDisposition] = useState<OwnerDisposition>("remove");
  const [error, setError] = useState<string | null>(null);
  const name = link.other_family_name ?? "this circle";

  const revoke = useMutation({
    mutationFn: () => revokeFamilyLink(link.link_id, disposition),
    onSuccess: onDone,
    onError: (err) =>
      setError(
        extractApiError(err)?.message ??
          "Couldn't unlink right now. Please try again.",
      ),
  });

  return (
    <Modal title={`Unlink from ${name}?`} onClose={onClose}>
      <p className="font-sans text-[14px] leading-[22px] text-ink-soft">
        Memories stop flowing between your circles. You choose what
        happens to what they've already shared with you.
      </p>
      <div className="mt-5">
        <SectionLabel>Their memories in your feed</SectionLabel>
        <Card className="overflow-hidden border border-sand/40">
          <div role="radiogroup" aria-label="Their memories in your feed">
            <RadioRow
              label="Keep them"
              sub="What they've shared stays visible to your circle."
              selected={disposition === "keep"}
              onSelect={() => setDisposition("keep")}
            />
            <Hairline />
            <RadioRow
              label="Remove them"
              sub="What they've shared no longer appears in your feed."
              selected={disposition === "remove"}
              onSelect={() => setDisposition("remove")}
            />
          </div>
        </Card>
      </div>
      <ErrorText>{error}</ErrorText>
      <div className="mt-6 flex justify-end gap-3">
        <QuietButton onClick={onClose}>Cancel</QuietButton>
        <DangerButton
          onClick={() => revoke.mutate()}
          disabled={revoke.isPending}
        >
          {revoke.isPending ? "Unlinking…" : "Unlink"}
        </DangerButton>
      </div>
    </Modal>
  );
};

export const Route = createFileRoute("/links")({
  loader: ({ context }) => requireAuth(context.queryClient),
  component: LinksPage,
});
